import { DEG, TAU, clamp01 } from '../math';
import { parseHex } from '../color';
import { createRng } from '../rng';
import type { ParamSchema } from '../params/types';
import type { PostContext, PostEffect } from './types';

/**
 * The post-processing effects.
 *
 * All of them work on `ImageData` unless they genuinely need canvas compositing
 * (bloom, halftone, paper). Reading and writing a 1080×1350 buffer is ~1.5 M pixels,
 * which is a couple of milliseconds per pass — acceptable because the chain only runs
 * when the composition redraws, not continuously.
 */

const num = (ctx: PostContext, key: string, fallback: number): number => {
  const v = ctx.values[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
};
const str = (ctx: PostContext, key: string, fallback: string): string => {
  const v = ctx.values[key];
  return typeof v === 'string' ? v : fallback;
};
const bool = (ctx: PostContext, key: string, fallback: boolean): boolean => {
  const v = ctx.values[key];
  return typeof v === 'boolean' ? v : fallback;
};

// ── Bloom ────────────────────────────────────────────────────────────────────

const bloomParams = [
  { kind: 'float', key: 'threshold', label: 'Schwelle', min: 0, max: 1, step: 0.01, default: 0.62, tooltip: 'Ab welcher Helligkeit ein Pixel leuchtet.' },
  { kind: 'float', key: 'radius', label: 'Radius', min: 1, max: 120, step: 1, scale: 'log', default: 26, unit: 'px' },
  { kind: 'float', key: 'intensity', label: 'Intensität', min: 0, max: 3, step: 0.05, default: 0.85 },
] as const satisfies ParamSchema;

const bloom: PostEffect = {
  id: 'bloom',
  name: 'Bloom',
  description: 'Helle Bereiche strahlen weich über ihre Ränder hinaus.',
  params: bloomParams,
  apply(ctx) {
    const threshold = num(ctx, 'threshold', 0.62);
    const radius = num(ctx, 'radius', 26);
    const intensity = num(ctx, 'intensity', 0.85);
    if (intensity <= 0) return;

    const { canvas: bright, ctx: bctx } = ctx.scratch();
    // Isolate the bright pass, blur it, then add it back with `lighter`. The canvas
    // blur filter is a real Gaussian and runs on the GPU where available — far faster
    // than a JS separable blur at this radius.
    bctx.setTransform(1, 0, 0, 1, 0, 0);
    bctx.globalCompositeOperation = 'source-over';
    bctx.clearRect(0, 0, ctx.width, ctx.height);
    bctx.drawImage(ctx.ctx.canvas as CanvasImageSource, 0, 0);

    const img = bctx.getImageData(0, 0, ctx.width, ctx.height);
    const d = img.data;
    const cut = threshold * 255;
    for (let i = 0; i < d.length; i += 4) {
      const l = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
      if (l < cut) {
        d[i + 3] = 0;
      } else {
        // Fade in above the threshold instead of clipping, or the bloom gets a hard edge.
        const k = Math.min(1, (l - cut) / Math.max(1, 255 - cut) + 0.2);
        d[i + 3] = Math.round(d[i + 3] * k);
      }
    }
    bctx.putImageData(img, 0, 0);

    ctx.ctx.save();
    ctx.ctx.globalCompositeOperation = 'lighter';
    ctx.ctx.globalAlpha = clamp01(intensity);
    ctx.ctx.filter = `blur(${radius}px)`;
    ctx.ctx.drawImage(bright as CanvasImageSource, 0, 0);
    if (intensity > 1) {
      ctx.ctx.globalAlpha = clamp01(intensity - 1);
      ctx.ctx.drawImage(bright as CanvasImageSource, 0, 0);
    }
    ctx.ctx.filter = 'none';
    ctx.ctx.restore();
  },
};

// ── Chromatic aberration ─────────────────────────────────────────────────────

const chromaticParams = [
  { kind: 'float', key: 'offset', label: 'Versatz', min: 0, max: 40, step: 0.5, default: 4, unit: 'px' },
  { kind: 'float', key: 'angle', label: 'Richtung', min: -180, max: 180, step: 1, default: 0, unit: '°' },
  { kind: 'bool', key: 'radial', label: 'Radial', default: true, tooltip: 'Versatz wächst zum Bildrand hin, wie bei einer echten Linse.' },
] as const satisfies ParamSchema;

const chromatic: PostEffect = {
  id: 'chromatic',
  name: 'Chromatische Aberration',
  description: 'Rot und Blau laufen gegeneinander auseinander.',
  params: chromaticParams,
  apply(ctx) {
    const offset = num(ctx, 'offset', 4);
    if (offset <= 0) return;
    const angle = num(ctx, 'angle', 0) * DEG;
    const radial = bool(ctx, 'radial', true);

    const { width, height } = ctx;
    const src = ctx.ctx.getImageData(0, 0, width, height);
    const out = ctx.ctx.createImageData(width, height);
    const s = src.data;
    const o = out.data;
    const cx = width / 2;
    const cy = height / 2;
    const maxR = Math.hypot(cx, cy);
    const dirX = Math.cos(angle);
    const dirY = Math.sin(angle);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        let ox: number;
        let oy: number;
        if (radial) {
          const dx = (x - cx) / maxR;
          const dy = (y - cy) / maxR;
          ox = dx * offset;
          oy = dy * offset;
        } else {
          ox = dirX * offset;
          oy = dirY * offset;
        }
        const rx = Math.min(width - 1, Math.max(0, Math.round(x + ox)));
        const ry = Math.min(height - 1, Math.max(0, Math.round(y + oy)));
        const bx = Math.min(width - 1, Math.max(0, Math.round(x - ox)));
        const by = Math.min(height - 1, Math.max(0, Math.round(y - oy)));
        o[i] = s[(ry * width + rx) * 4];
        o[i + 1] = s[i + 1];
        o[i + 2] = s[(by * width + bx) * 4 + 2];
        o[i + 3] = s[i + 3];
      }
    }
    ctx.ctx.putImageData(out, 0, 0);
  },
};

// ── Barrel distortion ────────────────────────────────────────────────────────

const distortParams = [
  { kind: 'float', key: 'amount', label: 'Stärke', min: -0.6, max: 0.6, step: 0.005, default: 0.12, tooltip: 'Positiv wölbt nach aussen (Tonne), negativ nach innen (Kissen).' },
] as const satisfies ParamSchema;

const barrel: PostEffect = {
  id: 'barrel',
  name: 'Verzeichnung',
  description: 'Wölbt das Bild tonnen- oder kissenförmig.',
  params: distortParams,
  apply(ctx) {
    const amount = num(ctx, 'amount', 0.12);
    if (Math.abs(amount) < 1e-4) return;

    const { width, height } = ctx;
    const src = ctx.ctx.getImageData(0, 0, width, height);
    const out = ctx.ctx.createImageData(width, height);
    const s = src.data;
    const o = out.data;
    const cx = width / 2;
    const cy = height / 2;
    const norm = Math.hypot(cx, cy);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const dx = (x - cx) / norm;
        const dy = (y - cy) / norm;
        const r2 = dx * dx + dy * dy;
        // Inverse mapping: for each destination pixel, find where it came from, so no
        // holes appear the way forward mapping would leave them.
        const k = 1 + amount * r2;
        const sx = cx + (dx / k) * norm;
        const sy = cy + (dy / k) * norm;
        const i = (y * width + x) * 4;
        if (sx < 0 || sy < 0 || sx >= width - 1 || sy >= height - 1) {
          o[i + 3] = 0;
          continue;
        }
        // Bilinear sample; nearest neighbour shows visible stair-stepping on type.
        const x0 = sx | 0;
        const y0 = sy | 0;
        const tx = sx - x0;
        const ty = sy - y0;
        const i00 = (y0 * width + x0) * 4;
        const i10 = i00 + 4;
        const i01 = i00 + width * 4;
        const i11 = i01 + 4;
        for (let c = 0; c < 4; c++) {
          const a = s[i00 + c] + (s[i10 + c] - s[i00 + c]) * tx;
          const b = s[i01 + c] + (s[i11 + c] - s[i01 + c]) * tx;
          o[i + c] = a + (b - a) * ty;
        }
      }
    }
    ctx.ctx.putImageData(out, 0, 0);
  },
};

// ── Halftone ─────────────────────────────────────────────────────────────────

const halftoneParams = [
  {
    kind: 'enum',
    key: 'shape',
    label: 'Rastertyp',
    default: 'dot',
    options: [
      { value: 'dot', label: 'Punkt' },
      { value: 'line', label: 'Linie' },
      { value: 'cross', label: 'Kreuz' },
      { value: 'square', label: 'Quadrat' },
    ],
  },
  { kind: 'float', key: 'pitch', label: 'Rasterweite', min: 2, max: 60, step: 0.5, default: 7, unit: 'px' },
  { kind: 'float', key: 'angle', label: 'Rasterwinkel', min: 0, max: 180, step: 1, default: 45, unit: '°' },
  { kind: 'float', key: 'mix', label: 'Anteil', min: 0, max: 1, step: 0.01, default: 1, tooltip: 'Überblendung zwischen Original und Raster.' },
  { kind: 'color', key: 'ink', label: 'Rasterfarbe', default: '#08080a' },
] as const satisfies ParamSchema;

const halftone: PostEffect = {
  id: 'halftone',
  name: 'Halftone',
  description: 'Legt ein Druckraster über das ganze Bild.',
  params: halftoneParams,
  apply(ctx) {
    const mix = num(ctx, 'mix', 1);
    if (mix <= 0) return;
    const pitch = Math.max(2, num(ctx, 'pitch', 7));
    const angle = num(ctx, 'angle', 45) * DEG;
    const shape = str(ctx, 'shape', 'dot');
    const ink = str(ctx, 'ink', '#08080a');

    const { width, height } = ctx;
    const src = ctx.ctx.getImageData(0, 0, width, height);
    const s = src.data;

    const { canvas: layer, ctx: lctx } = ctx.scratch();
    lctx.setTransform(1, 0, 0, 1, 0, 0);
    lctx.clearRect(0, 0, width, height);
    lctx.fillStyle = ink;
    lctx.strokeStyle = ink;

    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const cx = width / 2;
    const cy = height / 2;

    // Exact lattice bounds from the projection of the four canvas corners, rather than
    // a square the size of the diagonal — that overshoots by roughly 2× in cell count,
    // and the cells are the whole cost of this effect.
    let minI = Infinity;
    let maxI = -Infinity;
    let minJ = Infinity;
    let maxJ = -Infinity;
    for (const [px, py] of [
      [0, 0],
      [width, 0],
      [0, height],
      [width, height],
    ]) {
      const dx = px - cx;
      const dy = py - cy;
      const li = (dx * cos + dy * sin) / pitch;
      const lj = (-dx * sin + dy * cos) / pitch;
      if (li < minI) minI = li;
      if (li > maxI) maxI = li;
      if (lj < minJ) minJ = lj;
      if (lj > maxJ) maxJ = lj;
    }
    minI = Math.floor(minI) - 1;
    maxI = Math.ceil(maxI) + 1;
    minJ = Math.floor(minJ) - 1;
    maxJ = Math.ceil(maxJ) + 1;

    lctx.beginPath();
    for (let j = minJ; j <= maxJ; j++) {
      for (let i = minI; i <= maxI; i++) {
        const lx = i * pitch;
        const ly = j * pitch;
        const x = cx + lx * cos - ly * sin;
        const y = cy + lx * sin + ly * cos;
        if (x < -pitch || y < -pitch || x > width + pitch || y > height + pitch) continue;

        const px = Math.min(width - 1, Math.max(0, x | 0));
        const py = Math.min(height - 1, Math.max(0, y | 0));
        const k = (py * width + px) * 4;
        const lum = (s[k] * 0.299 + s[k + 1] * 0.587 + s[k + 2] * 0.114) / 255;
        // Dark areas print big dots.
        const size = (1 - lum) * pitch * 0.62;
        if (size < 0.15) continue;

        switch (shape) {
          case 'square':
            lctx.rect(x - size, y - size, size * 2, size * 2);
            break;
          case 'line':
            lctx.moveTo(x - pitch / 2 * cos, y - pitch / 2 * sin);
            lctx.lineTo(x + pitch / 2 * cos, y + pitch / 2 * sin);
            break;
          case 'cross':
            lctx.moveTo(x - size, y);
            lctx.lineTo(x + size, y);
            lctx.moveTo(x, y - size);
            lctx.lineTo(x, y + size);
            break;
          default:
            lctx.moveTo(x + size, y);
            lctx.arc(x, y, size, 0, TAU);
        }
      }
    }

    if (shape === 'line' || shape === 'cross') {
      lctx.lineWidth = Math.max(0.5, pitch * 0.34);
      lctx.lineCap = 'round';
      lctx.stroke();
    } else {
      lctx.fill();
    }

    ctx.ctx.save();
    ctx.ctx.globalAlpha = clamp01(mix);
    // Replace rather than overlay, so the raster reads as print and not as a screen.
    ctx.ctx.globalCompositeOperation = 'source-over';
    if (mix >= 1) {
      ctx.ctx.globalCompositeOperation = 'copy';
      ctx.ctx.globalAlpha = 1;
    }
    ctx.ctx.drawImage(layer as CanvasImageSource, 0, 0);
    ctx.ctx.restore();
  },
};

// ── Duotone / colour cast ────────────────────────────────────────────────────

const duotoneParams = [
  { kind: 'color', key: 'shadow', label: 'Tiefen', default: '#101a2e' },
  { kind: 'color', key: 'highlight', label: 'Lichter', default: '#ffe9c7' },
  { kind: 'float', key: 'amount', label: 'Stärke', min: 0, max: 1, step: 0.01, default: 0.8 },
] as const satisfies ParamSchema;

const duotone: PostEffect = {
  id: 'duotone',
  name: 'Duotone',
  description: 'Bildet die Helligkeit auf zwei Farben ab.',
  params: duotoneParams,
  apply(ctx) {
    const amount = num(ctx, 'amount', 0.8);
    if (amount <= 0) return;
    const shadow = parseHex(str(ctx, 'shadow', '#101a2e'));
    const highlight = parseHex(str(ctx, 'highlight', '#ffe9c7'));

    const img = ctx.ctx.getImageData(0, 0, ctx.width, ctx.height);
    const d = img.data;

    // A 256-entry table means the ramp is computed once instead of per pixel.
    const table = new Uint8Array(256 * 3);
    for (let i = 0; i < 256; i++) {
      const t = i / 255;
      table[i * 3] = Math.round((shadow.r + (highlight.r - shadow.r) * t) * 255);
      table[i * 3 + 1] = Math.round((shadow.g + (highlight.g - shadow.g) * t) * 255);
      table[i * 3 + 2] = Math.round((shadow.b + (highlight.b - shadow.b) * t) * 255);
    }

    // Perceptual lightness without a full OKLab conversion per pixel. Calling
    // `rgbToOklab` here costs three cube roots on 1.5 M pixels — measured at 137 ms.
    // For a greyscale L the transform collapses to `cbrt` of the linear luminance, so
    // two lookup tables replace every transcendental in the loop.
    for (let i = 0; i < d.length; i += 4) {
      const lin =
        0.2126 * SRGB_TO_LINEAR[d[i]] + 0.7152 * SRGB_TO_LINEAR[d[i + 1]] + 0.0722 * SRGB_TO_LINEAR[d[i + 2]];
      const k = LINEAR_TO_L[Math.min(1023, (lin * 1023) | 0)];
      d[i] += (table[k * 3] - d[i]) * amount;
      d[i + 1] += (table[k * 3 + 1] - d[i + 1]) * amount;
      d[i + 2] += (table[k * 3 + 2] - d[i + 2]) * amount;
    }
    ctx.ctx.putImageData(img, 0, 0);
  },
};

// ── Posterize ────────────────────────────────────────────────────────────────

const posterizeParams = [
  { kind: 'int', key: 'levels', label: 'Stufen', min: 2, max: 32, step: 1, default: 6 },
] as const satisfies ParamSchema;

const posterize: PostEffect = {
  id: 'posterize',
  name: 'Posterize',
  description: 'Reduziert jeden Farbkanal auf wenige Stufen.',
  params: posterizeParams,
  apply(ctx) {
    const levels = Math.max(2, Math.round(num(ctx, 'levels', 6)));
    const img = ctx.ctx.getImageData(0, 0, ctx.width, ctx.height);
    const d = img.data;
    const table = new Uint8Array(256);
    for (let i = 0; i < 256; i++) table[i] = Math.round(Math.round((i / 255) * (levels - 1)) * (255 / (levels - 1)));
    for (let i = 0; i < d.length; i += 4) {
      d[i] = table[d[i]];
      d[i + 1] = table[d[i + 1]];
      d[i + 2] = table[d[i + 2]];
    }
    ctx.ctx.putImageData(img, 0, 0);
  },
};

// ── Dither ───────────────────────────────────────────────────────────────────

const ditherParams = [
  {
    kind: 'enum',
    key: 'algorithm',
    label: 'Verfahren',
    default: 'bayer',
    options: [
      { value: 'bayer', label: 'Bayer 8×8' },
      { value: 'floyd', label: 'Floyd-Steinberg' },
      { value: 'noise', label: 'Blue Noise' },
    ],
  },
  { kind: 'int', key: 'levels', label: 'Stufen', min: 2, max: 8, step: 1, default: 2 },
  { kind: 'bool', key: 'monochrome', label: 'Monochrom', default: true },
] as const satisfies ParamSchema;

/** Standard 8×8 ordered Bayer threshold matrix, normalised to [0, 1). */
const BAYER8 = Float32Array.from(
  [
    0, 32, 8, 40, 2, 34, 10, 42,
    48, 16, 56, 24, 50, 18, 58, 26,
    12, 44, 4, 36, 14, 46, 6, 38,
    60, 28, 52, 20, 62, 30, 54, 22,
    3, 35, 11, 43, 1, 33, 9, 41,
    51, 19, 59, 27, 49, 17, 57, 25,
    15, 47, 7, 39, 13, 45, 5, 37,
    63, 31, 55, 23, 61, 29, 53, 21,
  ],
  (v) => v / 64,
);

const dither: PostEffect = {
  id: 'dither',
  name: 'Dither',
  description: 'Quantisiert das Bild mit einem sichtbaren Fehlerdiffusionsmuster.',
  params: ditherParams,
  apply(ctx) {
    const algorithm = str(ctx, 'algorithm', 'bayer');
    const levels = Math.max(2, Math.round(num(ctx, 'levels', 2)));
    const mono = bool(ctx, 'monochrome', true);
    const { width, height } = ctx;

    const img = ctx.ctx.getImageData(0, 0, width, height);
    const d = img.data;
    const step = 255 / (levels - 1);
    const rng = createRng(ctx.seed);

    if (algorithm === 'floyd') {
      // Floyd-Steinberg needs a float buffer: quantisation error is pushed forward and
      // would be lost to rounding in the byte array.
      const buf = new Float32Array(width * height * 3);
      for (let i = 0, p = 0; i < d.length; i += 4, p += 3) {
        if (mono) {
          const l = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
          buf[p] = buf[p + 1] = buf[p + 2] = l;
        } else {
          buf[p] = d[i];
          buf[p + 1] = d[i + 1];
          buf[p + 2] = d[i + 2];
        }
      }
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const p = (y * width + x) * 3;
          for (let c = 0; c < 3; c++) {
            const old = buf[p + c];
            const next = Math.round(old / step) * step;
            const err = old - next;
            buf[p + c] = next;
            // Weights 7/16, 3/16, 5/16, 1/16 over the four not-yet-visited neighbours.
            if (x + 1 < width) buf[p + 3 + c] += (err * 7) / 16;
            if (y + 1 < height) {
              const row = p + width * 3;
              if (x > 0) buf[row - 3 + c] += (err * 3) / 16;
              buf[row + c] += (err * 5) / 16;
              if (x + 1 < width) buf[row + 3 + c] += err / 16;
            }
          }
        }
      }
      for (let i = 0, p = 0; i < d.length; i += 4, p += 3) {
        d[i] = buf[p];
        d[i + 1] = buf[p + 1];
        d[i + 2] = buf[p + 2];
      }
    } else {
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const i = (y * width + x) * 4;
          const threshold = algorithm === 'noise' ? rng.next() : BAYER8[(y & 7) * 8 + (x & 7)];
          if (mono) {
            const l = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
            const v = Math.round(l / step + (threshold - 0.5)) * step;
            d[i] = d[i + 1] = d[i + 2] = Math.min(255, Math.max(0, v));
          } else {
            for (let c = 0; c < 3; c++) {
              const v = Math.round(d[i + c] / step + (threshold - 0.5)) * step;
              d[i + c] = Math.min(255, Math.max(0, v));
            }
          }
        }
      }
    }
    ctx.ctx.putImageData(img, 0, 0);
  },
};

// ── Film grain ───────────────────────────────────────────────────────────────

const grainParams = [
  { kind: 'float', key: 'amount', label: 'Stärke', min: 0, max: 1, step: 0.01, default: 0.18 },
  { kind: 'float', key: 'size', label: 'Körnung', min: 1, max: 8, step: 0.5, default: 1, unit: 'px' },
  { kind: 'bool', key: 'monochrome', label: 'Monochrom', default: true },
] as const satisfies ParamSchema;

const grain: PostEffect = {
  id: 'grain',
  name: 'Filmkorn',
  description: 'Legt Rauschen über das Bild.',
  params: grainParams,
  apply(ctx) {
    const amount = num(ctx, 'amount', 0.18);
    if (amount <= 0) return;
    const size = Math.max(1, num(ctx, 'size', 1));
    const mono = bool(ctx, 'monochrome', true);
    const { width, height } = ctx;

    const img = ctx.ctx.getImageData(0, 0, width, height);
    const d = img.data;
    const strength = amount * 110;
    const cell = Math.round(size);

    // Walk the noise pool with a seed-dependent stride so the pattern is reproducible
    // and different per seed, without generating a value per pixel.
    const rng = createRng(ctx.seed ^ 0x9e3779b9);
    let cursor = rng.int(0, NOISE_POOL_SIZE - 1);
    const stride = 1 + rng.int(0, 61) * 2; // odd, so it visits the whole pool

    // Coarse grain reuses one value per cell rather than blurring afterwards.
    const cells = Math.ceil(width / cell);
    const cache = new Float32Array(cells * 3);

    for (let y = 0; y < height; y++) {
      if (cell === 1 || y % cell === 0) {
        for (let i = 0; i < cache.length; i++) {
          cursor = (cursor + stride) % NOISE_POOL_SIZE;
          cache[i] = NOISE_POOL[cursor] * strength;
        }
      }
      const row = y * width * 4;
      for (let x = 0; x < width; x++) {
        const i = row + x * 4;
        const c = ((x / cell) | 0) * 3;
        if (mono) {
          const n = cache[c];
          d[i] = clampByte(d[i] + n);
          d[i + 1] = clampByte(d[i + 1] + n);
          d[i + 2] = clampByte(d[i + 2] + n);
        } else {
          d[i] = clampByte(d[i] + cache[c]);
          d[i + 1] = clampByte(d[i + 1] + cache[c + 1]);
          d[i + 2] = clampByte(d[i + 2] + cache[c + 2]);
        }
      }
    }
    ctx.ctx.putImageData(img, 0, 0);
  },
};

const clampByte = (v: number): number => (v < 0 ? 0 : v > 255 ? 255 : v);

// ── Shared lookup tables ─────────────────────────────────────────────────────
//
// Built once at module load. Every pixel-loop effect that needs a perceptual
// lightness reads these instead of doing the maths inline.

/** sRGB byte → linear light. */
const SRGB_TO_LINEAR = Float32Array.from({ length: 256 }, (_, i) => {
  const c = i / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
});

/** Linear luminance (1024 buckets) → OKLab L, quantised to a byte. */
const LINEAR_TO_L = Uint8Array.from({ length: 1024 }, (_, i) =>
  Math.min(255, Math.max(0, Math.round(Math.cbrt(i / 1023) * 255))),
);

/**
 * Pre-generated normal deviates for film grain.
 *
 * Box-Muller with rejection costs a loop and a log per sample; at one sample per grain
 * cell over a 1.5 M-pixel canvas that dominated the effect. A fixed pool sampled with a
 * stride is visually indistinguishable — the pool is far larger than any structure the
 * eye can pick out of noise.
 */
const NOISE_POOL_SIZE = 8192;
const NOISE_POOL = (() => {
  const pool = new Float32Array(NOISE_POOL_SIZE);
  const rng = createRng(0x5eed);
  for (let i = 0; i < NOISE_POOL_SIZE; i++) pool[i] = rng.gaussian(0, 1);
  return pool;
})();

// ── Paper texture ────────────────────────────────────────────────────────────

const paperParams = [
  { kind: 'float', key: 'amount', label: 'Stärke', min: 0, max: 1, step: 0.01, default: 0.3 },
  { kind: 'float', key: 'scale', label: 'Faserlänge', min: 1, max: 40, step: 0.5, default: 9, unit: 'px' },
] as const satisfies ParamSchema;

const paper: PostEffect = {
  id: 'paper',
  name: 'Papierstruktur',
  description: 'Legt eine Faserstruktur wie von Büttenpapier darüber.',
  params: paperParams,
  apply(ctx) {
    const amount = num(ctx, 'amount', 0.3);
    if (amount <= 0) return;
    const scale = Math.max(1, num(ctx, 'scale', 9));
    const { width, height } = ctx;
    const rng = createRng(ctx.seed ^ 0x51ed270b);

    const { canvas: tex, ctx: tctx } = ctx.scratch();
    tctx.setTransform(1, 0, 0, 1, 0, 0);
    tctx.clearRect(0, 0, width, height);
    tctx.lineWidth = 1;

    // Short random strokes in two directions read as fibres; a pure noise field reads
    // as digital grain, which the grain effect already provides.
    const strokes = Math.round((width * height) / (scale * 60));
    tctx.beginPath();
    for (let i = 0; i < strokes; i++) {
      const x = rng.next() * width;
      const y = rng.next() * height;
      const a = rng.chance(0.5) ? rng.signed(0.25) : Math.PI / 2 + rng.signed(0.25);
      const len = scale * (0.5 + rng.next());
      tctx.moveTo(x, y);
      tctx.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len);
    }
    tctx.strokeStyle = `rgba(255,255,255,${(amount * 0.5).toFixed(3)})`;
    tctx.stroke();

    tctx.beginPath();
    for (let i = 0; i < strokes; i++) {
      const x = rng.next() * width;
      const y = rng.next() * height;
      const a = rng.chance(0.5) ? rng.signed(0.25) : Math.PI / 2 + rng.signed(0.25);
      const len = scale * (0.5 + rng.next());
      tctx.moveTo(x, y);
      tctx.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len);
    }
    tctx.strokeStyle = `rgba(0,0,0,${(amount * 0.5).toFixed(3)})`;
    tctx.stroke();

    ctx.ctx.save();
    ctx.ctx.globalCompositeOperation = 'overlay';
    ctx.ctx.drawImage(tex as CanvasImageSource, 0, 0);
    ctx.ctx.restore();
  },
};

// ── Vignette ─────────────────────────────────────────────────────────────────

const vignetteParams = [
  { kind: 'float', key: 'amount', label: 'Stärke', min: 0, max: 1, step: 0.01, default: 0.4 },
  { kind: 'float', key: 'radius', label: 'Radius', min: 0.1, max: 1.5, step: 0.01, default: 0.72 },
  { kind: 'float', key: 'softness', label: 'Weichheit', min: 0.01, max: 1, step: 0.01, default: 0.55 },
  { kind: 'color', key: 'color', label: 'Farbe', default: '#000000' },
] as const satisfies ParamSchema;

const vignette: PostEffect = {
  id: 'vignette',
  name: 'Vignette',
  description: 'Dunkelt die Ränder ab.',
  params: vignetteParams,
  apply(ctx) {
    const amount = num(ctx, 'amount', 0.4);
    if (amount <= 0) return;
    const radius = num(ctx, 'radius', 0.72);
    const softness = num(ctx, 'softness', 0.55);
    const color = parseHex(str(ctx, 'color', '#000000'));
    const { width, height } = ctx;

    const outer = Math.hypot(width, height) / 2;
    const g = ctx.ctx.createRadialGradient(
      width / 2,
      height / 2,
      outer * Math.max(0, radius - softness),
      width / 2,
      height / 2,
      outer * Math.max(0.01, radius + softness * 0.5),
    );
    const rgb = `${Math.round(color.r * 255)},${Math.round(color.g * 255)},${Math.round(color.b * 255)}`;
    g.addColorStop(0, `rgba(${rgb},0)`);
    g.addColorStop(1, `rgba(${rgb},${amount})`);

    ctx.ctx.save();
    ctx.ctx.globalCompositeOperation = 'source-over';
    ctx.ctx.fillStyle = g;
    ctx.ctx.fillRect(0, 0, width, height);
    ctx.ctx.restore();
  },
};

/**
 * The chain, in application order.
 *
 * Optical effects first (they simulate the lens), then tonal work, then film
 * artefacts, with the vignette last so it darkens everything above it — the order a
 * photographic pipeline would apply them in.
 */
export const POST_EFFECTS: PostEffect[] = [
  barrel,
  chromatic,
  bloom,
  duotone,
  posterize,
  halftone,
  dither,
  paper,
  grain,
  vignette,
];

export const POST_EFFECT_BY_ID = new Map(POST_EFFECTS.map((e) => [e.id, e]));
